import { highlightEl, VxHighlightOptions } from './highlight.js';
import { clearOverlay } from './overlay.js';
import { VxNode } from './parser.js';
import { renderVxNode, VxRenderOptions } from './render.js';
import { resolveDomNode } from './snapshot.js';
import { traverseVxNode, TraverseVxNodeOptions } from './traverse.js';
import { isContainerNode, normalizeRef } from './util.js';

export class VxTreeView {

    private refMap = new Map<string, VxNode>();

    constructor(
        public nodes: VxNode[],
        public frameId: string,
        public iframeRef?: string,
    ) {
        this.buildRefMap(nodes);
    }

    get nodeCount() {
        return this.refMap.size;
    }

    *traverse(options: TraverseVxNodeOptions = {}): IterableIterator<{ vxNode: VxNode; depth: number }> {
        for (const vxNode of this.nodes) {
            yield* traverseVxNode(vxNode, options);
        }
    }

    private buildRefMap(nodes: VxNode[]) {
        for (const node of nodes) {
            if (node.ref) {
                this.refMap.set(node.ref, node);
            }
            this.buildRefMap(node.children ?? []);
        }
    }

    hasRef(ref: string): boolean {
        const nref = normalizeRef(ref);
        return this.refMap.has(nref);
    }

    findNode(ref: string): VxNode | null {
        const nref = normalizeRef(ref);
        return this.refMap.get(nref) ?? null;
    }

    render(options: VxRenderOptions = {}) {
        return this.nodes.map(node => {
            return renderVxNode(node, options);
        }).join('\n');
    }

    highlight(options: VxHighlightOptions = {}) {
        if (options.clearOverlay) {
            clearOverlay();
        }
        const leafOnly = !options.includeAll;
        const refs = this.collectRefs(leafOnly, options.filterRefs);
        this.highlightRefs(refs, options);
    }

    highlightRefs(refs: string[], options: VxHighlightOptions = {}) {
        for (const ref of refs) {
            const el = resolveDomNode(ref);
            if (el instanceof Element) {
                const color = options.useColors ? undefined : '#060';
                highlightEl(el, ref, color);
            }
        }
    }

    collectRefs(leafOnly = true, filterRefs?: string[]) {
        const refs: string[] = [];
        filterRefs = filterRefs?.map(ref => normalizeRef(ref));
        for (const { vxNode } of this.traverse()) {
            if (!vxNode.ref) {
                continue;
            }
            if (leafOnly && isContainerNode(vxNode)) {
                continue;
            }
            if (filterRefs && !filterRefs.includes(vxNode.ref)) {
                continue;
            }
            refs.push(vxNode.ref);
        }
        return refs;
    }

}
